<script setup>
import { computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  message: { type: String, default: '' },
  action: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const MAX_MESSAGE_LINE_LENGTH = 55;

const wrapLine = line => {
  const lines = [];
  let remaining = line;

  while (remaining.length > MAX_MESSAGE_LINE_LENGTH) {
    let breakAt = remaining.lastIndexOf(' ', MAX_MESSAGE_LINE_LENGTH);
    if (breakAt <= 0) breakAt = MAX_MESSAGE_LINE_LENGTH;

    lines.push(remaining.slice(0, breakAt).trimEnd());
    remaining = remaining.slice(breakAt).trimStart();
  }

  lines.push(remaining);
  return lines;
};

const formattedMessage = computed(() =>
  props.message.split(/\r?\n/).flatMap(wrapLine).join('\n')
);

const variant = computed(() => {
  const requestedVariant =
    props.action?.variant?.toLowerCase?.() ||
    props.action?.messageType?.toLowerCase?.() ||
    'info';

  if (requestedVariant === 'danger') return 'error';
  if (requestedVariant === 'info') return 'success';
  if (['success', 'error', 'warning'].includes(requestedVariant)) {
    return requestedVariant;
  }

  return 'warning';
});

const variantClasses = computed(() => {
  const variants = {
    success: 'bg-[rgb(219,245,225)] text-black',
    warning:
      'bg-n-amber-3 text-n-amber-12 dark:bg-n-amber-4/30 dark:text-n-amber-11',
    error: 'bg-[rgb(255,212,209)] text-black',
  };

  return variants[variant.value] || variants.warning;
});

const iconName = computed(() => {
  const icons = {
    success: 'i-lucide-circle-check',
    warning: 'i-lucide-triangle-alert',
    error: 'i-lucide-circle-alert',
  };

  return icons[variant.value] || icons.warning;
});

const iconClass = computed(() => {
  const classes = {
    success: 'text-[rgb(44,165,74)]',
    warning: 'text-n-amber-10 dark:text-n-amber-11',
    error: 'text-[rgb(255,56,46)]',
  };

  return classes[variant.value] || classes.warning;
});

const hasActionLink = computed(
  () => props.action?.type === 'link' && props.action?.message
);
</script>

<template>
  <div
    class="pointer-events-auto inline-flex min-h-[52px] max-w-full items-center gap-3 self-start rounded-xl p-3 shadow-lg"
    :class="variantClasses"
  >
    <div class="flex size-5 shrink-0 items-center justify-center">
      <Icon :icon="iconName" class="size-5" :class="iconClass" />
    </div>

    <div class="min-w-0">
      <p
        class="m-0 max-w-[55ch] whitespace-pre-line break-words text-sm font-normal leading-5"
      >
        {{ formattedMessage }}
      </p>
      <router-link
        v-if="hasActionLink"
        :to="action.to"
        class="mt-1 inline-flex text-sm font-normal underline underline-offset-2"
      >
        {{ action.message }}
      </router-link>
    </div>

    <NextButton
      slate
      ghost
      size="compact"
      class="!size-7 shrink-0 self-center !p-0"
      @click="emit('close')"
    >
      <template #icon>
        <Icon icon="i-lucide-x" class="size-5 shrink-0" />
      </template>
    </NextButton>
  </div>
</template>
